import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRegistrations } from '../context/RegistrationsContext';
import { useT } from '../i18n';
import { CalendarPrompt, driverCatMatchesScheduleCat } from '../components/CalendarUtils';

import { CATEGORIES, CAT_STYLE } from '../categories';

// ── Weather Widget ─────────────────────────────────────────────────────────────
// Shows Open-Meteo 14-day daily forecast. No API key required.
function WeatherWidget({ event, token, compact = false }) {
  const [weather, setWeather] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const q = event.location || event.start_address;
  const diffDays = (new Date(event.date) - Date.now()) / 86400000;

  React.useEffect(() => {
    console.log('[WeatherWidget] event.id=', event.id, 'q=', q, 'token=', !!token, 'diffDays=', diffDays.toFixed(1));
    if (!q) { console.log('[WeatherWidget] skipping: no location/start_address'); return; }
    if (!token) { console.log('[WeatherWidget] skipping: no token'); return; }
    if (diffDays < -1 || diffDays > 14) { console.log('[WeatherWidget] skipping: diffDays out of range', diffDays.toFixed(1)); return; }

    // Use noon UTC on the event date as the target timestamp
    const evDate = new Date(event.date);
    evDate.setUTCHours(12, 0, 0, 0);
    const dt = Math.floor(evDate.getTime() / 1000);
    console.log('[WeatherWidget] fetching weather for', q, 'dt=', dt);

    setLoading(true);
    fetch(`/api/weather?q=${encodeURIComponent(q)}&dt=${dt}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        console.log('[WeatherWidget] response status', r.status);
        return r.ok ? r.json() : r.json().then(e => Promise.reject(e));
      })
      .then(d => { console.log('[WeatherWidget] data', d); setWeather(d); })
      .catch(e => console.warn('[WeatherWidget] fetch failed', e))
      .finally(() => setLoading(false));
  }, [event.id]); // eslint-disable-line

  // No location at all → show hint for admin so they know to add one
  if (!q) {
    if (diffDays >= 0 && diffDays <= 14) {
      return (
        <div style={{ marginTop: compact ? 8 : 14, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.35)', letterSpacing: '0.08em' }}>
          WETTER: ORT HINZUFÜGEN UM VORHERSAGE ZU SEHEN
        </div>
      );
    }
    return null;
  }
  if (diffDays < -1 || diffDays > 14) return null;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.35, marginTop: compact ? 8 : 12 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#b9cacb', letterSpacing: '0.1em' }}>WETTER LÄDT…</span>
    </div>
  );
  if (!weather) return null;

  const details = [
    weather.wind_kph > 0  && `${weather.wind_kph} km/h Wind`,
    weather.rain_pct > 0  && `${weather.rain_pct}% Regen`,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: compact ? 6 : 10,
      background: 'rgba(var(--brand-primary-rgb),0.05)',
      border: '1px solid rgba(var(--brand-primary-rgb),0.15)',
      padding: compact ? '6px 10px' : '8px 14px',
      marginTop: compact ? 8 : 14,
      borderRadius: 3,
    }}>
      <span style={{ fontSize: compact ? 24 : 30, lineHeight: 1, flexShrink: 0 }}>{weather.emoji}</span>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: compact ? 15 : 19, fontWeight: 700, color: '#e2e2e8', lineHeight: 1 }}>
            {weather.temp_min}° / {weather.temp_max}°C
          </span>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 3 }}>
          {weather.description}{details ? ` · ${details}` : ''}
        </div>
      </div>
    </div>
  );
}

/**
 * Resolve color style for a raw schedule category name (e.g. "Junioren U19", "Elite-Amateure A").
 * Tries exact lookup first, then maps to the canonical category via driverCatMatchesScheduleCat.
 */
function schedCatStyle(scheduleCatName) {
  if (CAT_STYLE[scheduleCatName]) return CAT_STYLE[scheduleCatName];
  const canonical = CATEGORIES.find(cat => driverCatMatchesScheduleCat(cat, scheduleCatName));
  return CAT_STYLE[canonical] || CAT_STYLE.Elite;
}

/* Mobile schedule dropdown */
function ScheduleDropdown({ sortedCats, catFilter, setCatFilter }) {
  const [open, setOpen] = useState(false);
  const selectedCat = catFilter ? (sortedCats.find(c => c.category === catFilter) || null) : null;
  const s = selectedCat ? schedCatStyle(selectedCat.category) : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger card */}
      <div
        className="ed-glass"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${s ? s.color : 'rgba(59,73,75,0.4)'}`, background: s ? s.bg : undefined, cursor: 'pointer' }}
      >
        <div>
          {selectedCat ? (
            <>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: s.color, letterSpacing: '0.1em', marginBottom: 2 }}>{selectedCat.category.toUpperCase()}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: '#ffffff' }}>{selectedCat.category}</div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>RENNKLASSE</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: '#b9cacb' }}>Alle Klassen</div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {selectedCat && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 24, fontWeight: 700, color: selectedCat.start_time ? s.color : '#849495', letterSpacing: '-0.02em' }}>
              {selectedCat.start_time || '—'}
            </div>
          )}
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
        </div>
      </div>

      {/* Dropdown panel */}
      {open && (
        <>
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, border: '1px solid rgba(var(--brand-primary-rgb),0.18)', borderTop: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.85)', background: 'var(--surface-shell)', backdropFilter: 'none' }}>
            {/* "Alle Klassen" option */}
            <div
              onClick={() => { setCatFilter(null); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${!catFilter ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.3)'}`, background: !catFilter ? 'rgba(0,36,40,0.95)' : '#111318', cursor: 'pointer' }}
            >
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: !catFilter ? 'var(--brand-primary)' : '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>FILTER</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: !catFilter ? '#ffffff' : '#b9cacb' }}>Alle Klassen</div>
              </div>
              {!catFilter && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--brand-primary)' }}>✓</span>}
            </div>
            {sortedCats.map(({ category, start_time }) => {
              const sc = schedCatStyle(category);
              const isActive = catFilter === category;
              return (
                <div
                  key={category}
                  onClick={() => { setCatFilter(isActive ? null : category); setOpen(false); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${isActive ? sc.color : 'rgba(59,73,75,0.3)'}`, background: isActive ? sc.bg : '#111318', cursor: 'pointer', borderTop: '1px solid rgba(59,73,75,0.15)' }}
                >
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: sc.color, letterSpacing: '0.1em', marginBottom: 2 }}>{category.toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: isActive ? '#ffffff' : '#e2e2e8' }}>{category}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 24, fontWeight: 700, color: start_time ? (isActive ? sc.color : '#ffffff') : '#849495', letterSpacing: '-0.02em' }}>
                      {start_time || '—'}
                    </div>
                    {isActive && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: sc.color }}>✓</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Backdrop */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
        </>
      )}
    </div>
  );
}

/* Shared avatar: photo if available, else coloured initials box */
function DriverAvatar({ driver, size = 30, bg, border, color, initialsStyle = {} }) {
  const initials = (driver?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  if (driver?.photo_url) {
    return (
      <img
        src={driver.photo_url}
        alt={initials}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: border ? `1px solid ${border}` : 'none' }}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, background: bg || 'rgba(40,42,46,0.8)', border: `1px solid ${border || 'rgba(59,73,75,0.3)'}`, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "var(--font-display)", fontSize: size * 0.36, fontWeight: 800, color: color || '#849495', ...initialsStyle }}>
      {initials}
    </div>
  );
}

function EventDetail({ token, role, isCoAdmin = false, driverId, ownerDriverId, isRacing }) {
  const isAdmin = role === 'admin';
  const isAdminOrCoAdmin = isAdmin || isCoAdmin;
  const effectiveDriverId = isAdmin ? (isRacing ? ownerDriverId : null) : driverId;
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { registrations, register: ctxRegister, unregister: ctxUnregister, refresh: refreshRegs } = useRegistrations();

  const [event, setEvent]       = useState(null);
  const [allDrivers, setAllDrivers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [message, setMessage]   = useState({ text: '', type: '' }); // type: 'ok'|'err'
  const [opPending, setOpPending] = useState(null); // driverId currently being toggled
  const [catFilter, setCatFilter] = useState(null); // null = alle
  const [reminding, setReminding] = useState(false);
  const [showCalPrompt, setShowCalPrompt] = useState(false);

  // Fahrgemeinschaft
  const [fullEventRegs, setFullEventRegs] = useState([]);
  const [showCarpoolEdit, setShowCarpoolEdit] = useState(false);

  // Ergebnisse
  const [eventResults, setEventResults] = useState([]);       // { driver_id, position, points }[]
  const [resultDraft, setResultDraft] = useState({});         // { [driver_id]: { position, points } }
  const [resultSaving, setResultSaving] = useState(false);
  const [carpoolPending, setCarpoolPending] = useState(false);
  const [carpoolMode, setCarpoolMode] = useState('');
  const [carpoolSeats, setCarpoolSeats] = useState('');
  const [carpoolFrom, setCarpoolFrom] = useState('');
  const [carpoolTime, setCarpoolTime] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [evRes, drRes] = await Promise.all([
        axios.get(`/api/events/${id}`, { headers: h }),
        axios.get('/api/drivers',       { headers: h }),
      ]);
      setEvent(evRes.data);
      // Filter out the owner row if they're not racing — they have no business
      // appearing in participant lists or squad management when is_racing=false
      const drivers = (drRes.data || []).filter(d => !d._isOwner || d.is_racing);
      setAllDrivers(drivers.sort((a, b) => a.name.localeCompare(b.name)));
      await refreshRegs();
      await loadFullEventRegs(h);
      await loadResults(h);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadFullEventRegs = async (headers) => {
    try {
      const h = headers || { Authorization: `Bearer ${token}` };
      const res = await axios.get(`/api/registrations/event/${id}`, { headers: h });
      setFullEventRegs(res.data || []);
    } catch (e) {
      console.error('[carpool] loadFullEventRegs', e);
    }
  };

  const loadResults = async (headers) => {
    try {
      const h = headers || { Authorization: `Bearer ${token}` };
      const res = await axios.get(`/api/results/event/${id}`, { headers: h });
      const rows = res.data || [];
      setEventResults(rows);
      // Pre-fill draft from saved results
      const draft = {};
      rows.forEach(r => { draft[r.driver_id] = { position: r.position ?? '', points: r.points ?? '' }; });
      setResultDraft(draft);
    } catch (e) {
      console.error('[results] loadResults', e);
    }
  };

  const handleSaveResult = async (driverId) => {
    setResultSaving(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const d = resultDraft[driverId] || {};
      await axios.post('/api/results', {
        event_id: parseInt(id),
        driver_id: driverId,
        position: d.position !== '' && d.position !== undefined ? parseInt(d.position) : null,
        points: d.points !== '' && d.points !== undefined ? parseInt(d.points) : 0,
      }, { headers: h });
      await loadResults(h);
      flash('Ergebnis gespeichert ✓', 'ok');
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setResultSaving(false);
    }
  };

  const flash = (text, type = 'ok') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3500);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: event?.name || 'Event', url });
      } catch (e) {
        // user cancelled — no flash needed
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        flash('Link kopiert ✓', 'ok');
      } catch (e) {
        flash('Link konnte nicht kopiert werden', 'err');
      }
    }
  };

  const handleRemind = async () => {
    setReminding(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`/api/events/${id}/remind`, {}, { headers: h });
      flash(res.data.message, 'ok');
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setReminding(false);
    }
  };

  /* ── registration helpers ── */
  const eventRegs   = registrations.filter(r => String(r.event_id) === String(id));
  const getRegFor   = (dId) => eventRegs.find(r => r.driver_id === dId);
  const ownReg      = getRegFor(effectiveDriverId);
  const ownDriver   = allDrivers.find(d => d.id === effectiveDriverId);
  const ownCategory = ownDriver?.category || 'Elite';

  // All statuses shown in squad list (not just 'participating')
  const registeredDriverIds = new Set(eventRegs.map(r => r.driver_id));
  const participatingRegs   = eventRegs.filter(r => r.status === 'participating' || r.status === 'assigned');
  const notParticipating    = eventRegs.filter(r => r.status === 'not_participating');
  const pendingRegs         = eventRegs.filter(r => r.status === 'requested');
  const unregisteredDrivers = allDrivers.filter(d => !registeredDriverIds.has(d.id));

  const handleRegister = async (targetDriverId, status = 'participating') => {
    setOpPending(targetDriverId);
    try {
      await ctxRegister(parseInt(id), targetDriverId, status);
      flash(`${t('evdetail_registered')} ✓`, 'ok');
      // Show calendar prompt when driver signs themselves up
      if (status === 'participating' && targetDriverId === effectiveDriverId) {
        setShowCalPrompt(true);
      }
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setOpPending(null);
    }
  };

  const handleUnregister = async (reg, confirmed = false) => {
    if (!confirmed && !window.confirm(`${t('evdetail_confirm_unregister')} ${allDrivers.find(d => d.id === reg.driver_id)?.name || t('evdetail_this_rider')}?`)) return;
    setOpPending(reg.driver_id);
    try {
      await ctxUnregister(reg.id);
      flash(t('evdetail_unregistered_ok'), 'ok');
    } catch {
      flash(t('evdetail_unregister_error'), 'err');
    } finally {
      setOpPending(null);
    }
  };

  /* ── Own toggle (driver or racing owner) ── */
  const handleOwnToggle = async () => {
    if (ownReg?.status === 'participating' || ownReg?.status === 'assigned') {
      await handleUnregister(ownReg, true);
    } else {
      await handleRegister(effectiveDriverId, 'participating');
    }
  };

  /* ── Admin status cycle ── */
  const ADMIN_STATUSES = [
    { value: 'participating',     label: 'Angemeldet',  color: 'var(--brand-primary)', icon: '✓' },
    { value: 'not_participating', label: 'Abgemeldet',  color: '#ffb4ab', icon: '⊘' },
    { value: null,                label: 'Kein Status', color: '#849495', icon: '×' },
  ];

  const handleAdminStatusChange = async (driver, newStatus) => {
    setOpPending(driver.id);
    try {
      const existing = getRegFor(driver.id);
      if (newStatus === null) {
        if (existing) await ctxUnregister(existing.id);
      } else {
        await ctxRegister(parseInt(id), driver.id, newStatus);
      }
      flash('Status aktualisiert ✓', 'ok');
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setOpPending(null);
    }
  };

  /* ── Fahrgemeinschaft helpers ── */
  const ownFullReg          = fullEventRegs.find(r => r.driver_id === effectiveDriverId);
  const ownRidingWithId     = ownFullReg?.riding_with ?? null; // registration id
  const ownRidingWithReg    = ownRidingWithId ? fullEventRegs.find(r => r.id === ownRidingWithId) : null;
  const ownRidingWithDriver = ownRidingWithReg ? allDrivers.find(d => d.id === ownRidingWithReg.driver_id) : null;

  const openCarpoolEdit = () => {
    setCarpoolMode(ownFullReg?.transport_mode || '');
    setCarpoolSeats(ownFullReg?.transport_seats ? String(ownFullReg.transport_seats) : '');
    setCarpoolFrom(ownFullReg?.transport_from || '');
    setCarpoolTime(ownFullReg?.transport_time || '');
    setShowCarpoolEdit(true);
  };

  const handleSaveTransport = async () => {
    if (!ownReg || carpoolPending) return;
    setCarpoolPending(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/registrations/${ownReg.id}/transport`, {
        transport_mode: carpoolMode || null,
        transport_seats: carpoolMode === 'driving' && carpoolSeats ? parseInt(carpoolSeats) : null,
        transport_from:  carpoolMode === 'driving' && carpoolFrom ? carpoolFrom : null,
        transport_time:  carpoolMode === 'driving' && carpoolTime ? carpoolTime : null,
      }, { headers: h });
      await loadFullEventRegs();
      setShowCarpoolEdit(false);
      flash('Fahrgemeinschaft aktualisiert ✓', 'ok');
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setCarpoolPending(false);
    }
  };

  const handleClearTransport = async () => {
    if (!ownReg || carpoolPending) return;
    setCarpoolPending(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/registrations/${ownReg.id}/transport`, {
        transport_mode: null, transport_seats: null, transport_from: null, transport_time: null,
      }, { headers: h });
      await loadFullEventRegs();
      setShowCarpoolEdit(false);
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setCarpoolPending(false);
    }
  };

  const handleRideWith = async (targetRegId) => {
    if (!ownReg || carpoolPending) return;
    // If the driver currently has passengers, warn before releasing them
    const ownPassengers = fullEventRegs
      .filter(r => r.riding_with === ownReg.id)
      .map(r => allDrivers.find(d => d.id === r.driver_id)?.name)
      .filter(Boolean);
    if (ownPassengers.length > 0) {
      const names = ownPassengers.join(', ');
      const ok = window.confirm(
        `Du hast ${ownPassengers.length === 1 ? 'einen Mitfahrer' : `${ownPassengers.length} Mitfahrer`} (${names}), ${ownPassengers.length === 1 ? 'der' : 'die'} dann keinen Platz mehr ${ownPassengers.length === 1 ? 'hat' : 'haben'}.\n\nTrotzdem woanders mitfahren?`
      );
      if (!ok) return;
    }
    setCarpoolPending(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/registrations/${ownReg.id}/transport`, { riding_with: targetRegId }, { headers: h });
      await loadFullEventRegs();
      flash('Platz reserviert ✓', 'ok');
    } catch (err) {
      flash('Fehler: ' + (err.response?.data?.error || err.message), 'err');
    } finally {
      setCarpoolPending(false);
    }
  };

  const handleCancelRide = async () => {
    if (!ownReg || carpoolPending) return;
    setCarpoolPending(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/registrations/${ownReg.id}/transport`, { riding_with: null }, { headers: h });
      await loadFullEventRegs();
    } catch (err) {
      flash('Fehler', 'err');
    } finally {
      setCarpoolPending(false);
    }
  };

  /* ── Formatters ── */
  const fmtShort = (d) => new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();
  const fmtYear  = (d) => new Date(d).getFullYear();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--primary)', animation: 'edPulse 1.4s infinite' }}>
        {t('evdetail_loading').toUpperCase()}
      </span>
    </div>
  );
  if (!event) return (
    <div style={{ padding: '2rem', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--error)' }}>
      {t('evdetail_not_found').toUpperCase()}
    </div>
  );

  const evCats  = event.categories || [];
  const sortedCats = [...evCats].sort((a, b) => {
    if (!a.start_time && !b.start_time) return 0;
    if (!a.start_time) return 1;
    if (!b.start_time) return -1;
    return a.start_time.localeCompare(b.start_time);
  });
  const evDate  = new Date(event.date);
  const isToday = evDate.toDateString() === new Date().toDateString();
  const isPast  = !isToday && evDate < new Date();
  const ownIsIn = ownReg?.status === 'participating' || ownReg?.status === 'assigned';

  return (
    <>
      {showCalPrompt && event && (
        <CalendarPrompt
          ev={event}
          driverCategory={ownCategory}
          onDismiss={() => setShowCalPrompt(false)}
        />
      )}
      <style>{`
        @keyframes edPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes edGlow   { 0%,100%{box-shadow:0 0 0 0 rgba(var(--brand-primary-rgb),0.4)} 50%{box-shadow:0 0 0 6px rgba(var(--brand-primary-rgb),0)} }

        .ed-glass {
          background: rgba(30,32,36,0.55);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(var(--brand-primary-rgb),0.13);
        }
        .ed-btn-reg {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 14px 20px;
          font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; letter-spacing: 0.12em;
          background: var(--brand-primary); color: #002022; border: none; cursor: pointer;
          transition: box-shadow 0.2s, transform 0.1s;
        }
        .ed-btn-reg:hover { box-shadow: 0 0 20px rgba(var(--brand-primary-rgb),0.4); }
        .ed-btn-reg:active { transform: scale(0.98); }
        .ed-btn-unreg {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 14px 20px;
          font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; letter-spacing: 0.12em;
          background: transparent; color: #ffb4ab; border: 1px solid rgba(255,180,171,0.4); cursor: pointer;
          transition: background 0.2s;
        }
        .ed-btn-unreg:hover { background: rgba(255,180,171,0.08); }
        .ed-status-pill {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
          padding: 4px 10px; border: 1px solid; cursor: pointer;
          transition: opacity 0.15s;
        }
        .ed-status-pill:hover { opacity: 0.7; }
        .ed-driver-row { transition: background 0.15s; }
        .ed-driver-row:hover { background: rgba(var(--brand-primary-rgb),0.04) !important; }
        .ed-flash-ok  { background: rgba(var(--brand-primary-rgb),0.1);  border: 1px solid rgba(var(--brand-primary-rgb),0.3);  color: var(--brand-primary); }
        .ed-flash-err { background: rgba(147,0,10,0.15);  border: 1px solid rgba(255,180,171,0.3); color: #ffb4ab; }

        @media (max-width: 767px) {
          .ed-mobile  { display: block !important; }
          .ed-desktop { display: none  !important; }
        }
        @media (min-width: 768px) {
          .ed-mobile  { display: none  !important; }
          .ed-desktop { display: block !important; }
        }
      `}</style>

      {/* ══════════════════════ SHARED FLASH ══════════════════════ */}
      {message.text && (
        <div className={`ed-flash-${message.type}`} style={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', zIndex: 999, padding: '10px 20px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', whiteSpace: 'nowrap', backdropFilter: 'blur(12px)' }}>
          {message.text}
        </div>
      )}

      {/* ══════════════════════ MOBILE ══════════════════════ */}
      <div className="ed-mobile" style={{ display: 'none' }}>

        {/* Back + Share row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#849495', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            ← {t('evdetail_back_mobile').toUpperCase()}
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setShowCalPrompt(true)} style={{ background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
              ＋ KALENDER
            </button>
            <button onClick={handleShare} style={{ background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
              ⎗ {t('evdetail_share').toUpperCase()}
            </button>
          </div>
        </div>

        {/* ── Hero ── */}
        <div style={{ margin: '0 -16px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #050e10 0%, #071520 60%, #111318 100%)', borderBottom: '1px solid rgba(var(--brand-primary-rgb),0.12)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(var(--brand-primary-rgb),0.025) 40px), repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(var(--brand-primary-rgb),0.015) 80px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 60%, #111318 100%)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, padding: '28px 16px 24px' }}>
            {/* Status badge */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {event.is_cancelled && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: '#dc2626', color: '#fff' }}>
                  ✕ ABGESAGT
                </span>
              )}
              {event.is_mandatory && !event.is_cancelled && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: '#ff9a1f', color: '#1a0800' }}>
                  ⚠ {t('evdetail_mandatory').toUpperCase()}
                </span>
              )}
              {(isPast || isToday) && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: isPast ? 'rgba(59,73,75,0.4)' : 'rgba(var(--brand-primary-rgb),0.15)', border: `1px solid ${isPast ? 'rgba(59,73,75,0.4)' : 'rgba(var(--brand-primary-rgb),0.35)'}`, color: isPast ? '#849495' : 'var(--brand-primary)' }}>
                  {isPast ? t('evdetail_past').toUpperCase() : `● ${t('evdetail_today').toUpperCase()}`}
                </span>
              )}
              {event.track_type && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 10px', border: '1px solid rgba(59,73,75,0.5)', color: '#849495' }}>
                  {event.track_type.toUpperCase()}
                </span>
              )}
            </div>
            {/* Name */}
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 'clamp(24px, 7vw, 34px)', fontWeight: 900, fontStyle: 'italic', color: event.is_cancelled ? 'rgba(220,100,100,0.75)' : '#ffffff', lineHeight: 1.05, textTransform: 'uppercase', margin: '0 0 16px 0', letterSpacing: '0.02em', textDecoration: event.is_cancelled ? 'line-through' : 'none' }}>
              {event.name}
            </h1>
            {/* Meta row */}
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 2 }}>{t('evdetail_field_date').toUpperCase()}</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)' }}>{fmtShort(event.date)} {fmtYear(event.date)}</div>
              </div>
              {event.location && (
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 2 }}>{t('evdetail_field_location').toUpperCase()}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: '#e2e2e8' }}>{event.location.toUpperCase()}</div>
                </div>
              )}
              {event.start_address && (
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>START / ZIEL</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: '#e2e2e8', marginBottom: 5 }}>{event.start_address}</div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.start_address)}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--brand-primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '3px 8px', textDecoration: 'none' }}>
                      ↗ APPLE MAPS
                    </a>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.start_address)}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--brand-primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '3px 8px', textDecoration: 'none' }}>
                      ↗ GOOGLE MAPS
                    </a>
                  </div>
                </div>
              )}
              {event.track_length && (
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 2 }}>{t('evdetail_field_length').toUpperCase()}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: '#e2e2e8' }}>{Number(event.track_length).toLocaleString('de-DE')} m</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Weather row — sits between hero and HUD stats ── */}
        <div style={{ padding: '10px 16px', background: 'rgba(0,5,8,0.6)', borderBottom: '1px solid rgba(var(--brand-primary-rgb),0.1)' }}>
          <WeatherWidget event={event} token={token} compact />
        </div>

        {/* ── HUD Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: 'var(--surface-shell)', borderBottom: '1px solid rgba(var(--brand-primary-rgb),0.12)' }}>
          {[
            { label: t('evdetail_stat_registered').toUpperCase(), value: participatingRegs.length, color: 'var(--brand-primary)' },
            { label: t('evdetail_stat_classes').toUpperCase(),    value: evCats.length || '—',       color: '#e2e2e8' },
            { label: t('evdetail_stat_status').toUpperCase(),     value: isPast ? 'PAST' : isToday ? 'LIVE' : 'OPEN',   color: isPast ? '#849495' : isToday ? 'var(--brand-primary)' : 'var(--brand-secondary)' },
          ].map(({ label, value, color }, i) => (
            <div key={label} style={{ padding: '12px 8px', textAlign: 'center', borderRight: i < 2 ? '1px solid rgba(var(--brand-primary-rgb),0.1)' : 'none' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>{label}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Description ── */}
        {event.description && (
          <div style={{ padding: '16px 0' }}>
            <p style={{ fontFamily: "var(--font-display)", fontSize: 14, color: '#b9cacb', lineHeight: 1.6, margin: 0 }}>{event.description}</p>
          </div>
        )}

        {/* ── Fahrer + Co-Admin + Racing Owner: Eigene Anmeldung ── */}
        {(!isAdmin || isCoAdmin || (isAdmin && effectiveDriverId)) && (
          <section style={{ paddingBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495', marginBottom: 10 }}>{t('evdetail_my_reg').toUpperCase()}</div>
            {isPast ? (
              <div className="ed-glass" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: ownIsIn ? 'var(--brand-primary)' : '#849495' }}>
                  {ownIsIn ? (isToday ? `✓ ${t('evdetail_is_reg').toUpperCase()}` : `✓ ${t('evdetail_was_reg').toUpperCase()}`) : `— ${t('evdetail_not_reg').toUpperCase()}`}
                </span>
              </div>
            ) : ownIsIn ? (
              <div className="ed-glass" style={{ overflow: 'hidden', borderLeft: '3px solid var(--brand-secondary)' }}>
                <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--brand-secondary)', letterSpacing: '0.1em', marginBottom: 3 }}>✓ {t('evdetail_registered').toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 14, color: '#e2e2e8' }}>{ownDriver?.name}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).color, letterSpacing: '0.08em', marginTop: 2 }}>{ownCategory.toUpperCase()}</div>
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: 'var(--brand-secondary)' }}>✓</div>
                </div>
                <button onClick={handleOwnToggle} disabled={opPending === effectiveDriverId} className="ed-btn-unreg">
                  {opPending === effectiveDriverId ? '…' : `✕  ${t('evdetail_unregister').toUpperCase()}`}
                </button>
              </div>
            ) : ownReg?.status === 'not_participating' ? (
              <div className="ed-glass" style={{ overflow: 'hidden', borderLeft: '3px solid #ffb4ab' }}>
                <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: '#ffb4ab', letterSpacing: '0.1em', marginBottom: 3 }}>⊘ {t('evdetail_cancelled').toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 14, color: '#e2e2e8' }}>{ownDriver?.name}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).color, letterSpacing: '0.08em', marginTop: 2 }}>{ownCategory.toUpperCase()}</div>
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: '#ffb4ab' }}>⊘</div>
                </div>
                <button onClick={() => handleRegister(effectiveDriverId, 'participating')} disabled={opPending === effectiveDriverId} className="ed-btn-reg">
                  {opPending === effectiveDriverId ? '…' : `+ ${t('evdetail_register_anyway').toUpperCase()}`}
                </button>
              </div>
            ) : (
              <div className="ed-glass" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', letterSpacing: '0.1em', marginBottom: 3 }}>{t('evdetail_no_response').toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 14, color: '#b9cacb' }}>{ownDriver?.name}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).color, letterSpacing: '0.08em', marginTop: 2 }}>{ownCategory.toUpperCase()}</div>
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <button onClick={() => handleRegister(effectiveDriverId, 'participating')} disabled={opPending === effectiveDriverId} className="ed-btn-reg" style={{ flex: 1, borderRight: '1px solid rgba(59,73,75,0.4)' }}>
                    {opPending === effectiveDriverId ? '…' : `✓ ${t('evdetail_im_in').toUpperCase()}`}
                  </button>
                  <button onClick={() => handleRegister(effectiveDriverId, 'not_participating')} disabled={opPending === effectiveDriverId} className="ed-btn-unreg" style={{ flex: 1 }}>
                    {opPending === effectiveDriverId ? '…' : `✕ ${t('evdetail_cant_make_it').toUpperCase()}`}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Event Schedule — Dropdown Filter (mobile) ── */}
        {evCats.length > 0 && (
          <section style={{ paddingBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495', marginBottom: 10 }}>EVENT SCHEDULE</div>
            <ScheduleDropdown sortedCats={sortedCats} catFilter={catFilter} setCatFilter={setCatFilter} />
          </section>
        )}

        {/* ── Squad ── */}
        <section style={{ paddingBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495' }}>{isAdminOrCoAdmin ? t('evdetail_rider_status').toUpperCase() : 'SQUAD'}</div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--brand-primary)', letterSpacing: '0.08em' }}>{participatingRegs.length} RIDER{participatingRegs.length !== 1 ? 'S' : ''}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {CATEGORIES.map(cat => {
              const s = CAT_STYLE[cat] || CAT_STYLE.Elite;
              if (isAdminOrCoAdmin) {
                const driversInCat = allDrivers.filter(d => (d.category || 'Elite') === cat);
                if (driversInCat.length === 0) return null;
                if (catFilter && !driverCatMatchesScheduleCat(cat, catFilter)) return null;
                const pending = driversInCat.filter(d => { const r = getRegFor(d.id); return !r || (r.status !== 'participating' && r.status !== 'assigned' && r.status !== 'not_participating'); });
                const inCat   = driversInCat.filter(d => { const r = getRegFor(d.id); return r?.status === 'participating' || r?.status === 'assigned'; });
                const outCat  = driversInCat.filter(d => { const r = getRegFor(d.id); return r?.status === 'not_participating'; });
                const ordered = [...pending, ...inCat, ...outCat];
                return (
                  <div key={cat}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${s.border}` }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: s.color }}>{cat.toUpperCase()}</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: s.color, opacity: 0.6, marginLeft: 'auto' }}>{inCat.length}/{driversInCat.length} {t('evdetail_in_short')}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ordered.map(driver => {
                        const reg    = getRegFor(driver.id);
                        const status = reg?.status ?? null;
                        const isIn   = status === 'participating' || status === 'assigned';
                        const isOut  = status === 'not_participating';
                        return (
                          <div key={driver.id} className="ed-glass ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderLeft: isIn ? `3px solid ${s.border}` : isOut ? '3px solid rgba(255,180,171,0.2)' : '3px solid rgba(59,73,75,0.3)', opacity: isOut ? 0.65 : 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <DriverAvatar driver={driver} size={30} bg={isIn ? s.bg : isOut ? 'rgba(255,180,171,0.07)' : 'rgba(40,42,46,0.8)'} border={isIn ? s.border : isOut ? 'rgba(255,180,171,0.2)' : 'rgba(59,73,75,0.3)'} color={isIn ? s.color : isOut ? '#ffb4ab' : '#849495'} />
                              <span style={{ fontFamily: "var(--font-display)", fontSize: 13, color: isIn ? '#e2e2e8' : '#849495', textDecoration: isOut ? 'line-through' : 'none' }}>{driver.name}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              {ADMIN_STATUSES.map(({ value, label, color, icon }) => {
                                const isActive = status === value || (value === null && status === null);
                                return (
                                  <button key={String(value)} onClick={() => !isActive && handleAdminStatusChange(driver, value)} disabled={opPending === driver.id || isActive} title={label}
                                    style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, background: isActive ? `rgba(${value === 'participating' ? '0,219,233' : value === 'not_participating' ? '255,180,171' : '171,214,0'},0.15)` : 'transparent', border: `1px solid ${isActive ? color : 'rgba(59,73,75,0.3)'}`, color: isActive ? color : 'rgba(132,148,149,0.4)', cursor: isActive ? 'default' : 'pointer' }}>
                                    {opPending === driver.id ? '…' : icon}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Fahrer/Viewer view
              const catIn  = participatingRegs.filter(r => (allDrivers.find(d => d.id === r.driver_id)?.category || 'Elite') === cat);
              const catOut = notParticipating.filter(r => (allDrivers.find(d => d.id === r.driver_id)?.category || 'Elite') === cat);
              if (catIn.length === 0 && catOut.length === 0) return null;
              if (catFilter && !driverCatMatchesScheduleCat(cat, catFilter)) return null;
              return (
                <div key={cat}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${s.border}` }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: s.color }}>{cat.toUpperCase()}</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: s.color, opacity: 0.6, marginLeft: 'auto' }}>{catIn.length} {t('evdetail_in_short')}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {catIn.map(reg => {
                      const driver = allDrivers.find(d => d.id === reg.driver_id);
                      if (!driver) return null;
                      const isMe = driver.id === driverId;
                      return (
                        <div key={reg.id} className="ed-glass ed-driver-row" style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderLeft: isMe ? '3px solid var(--brand-secondary)' : `3px solid ${s.border}` }}>
                          <div style={{ marginRight: 12, flexShrink: 0 }}><DriverAvatar driver={driver} size={34} bg={s.bg} border={s.border} color={s.color} /></div>
                          <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 600, color: '#e2e2e8' }}>{driver.name}</span>
                          {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: 'var(--brand-secondary)', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)', padding: '1px 5px', marginLeft: 6 }}>{t('drivers_badge_me')}</span>}
                        </div>
                      );
                    })}
                    {catOut.map(reg => {
                      const driver = allDrivers.find(d => d.id === reg.driver_id);
                      if (!driver) return null;
                      const isMe = driver.id === driverId;
                      return (
                        <div key={reg.id} className="ed-glass ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderLeft: isMe ? '3px solid #ffb4ab' : '3px solid rgba(255,180,171,0.2)', opacity: 0.65 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <DriverAvatar driver={driver} size={34} bg="rgba(255,180,171,0.06)" border="rgba(255,180,171,0.2)" color="#ffb4ab" />
                            <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 600, color: '#849495', textDecoration: 'line-through' }}>{driver.name}</span>
                            {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: '#ffb4ab', background: 'rgba(255,180,171,0.1)', border: '1px solid rgba(255,180,171,0.25)', padding: '1px 5px' }}>ICH</span>}
                          </div>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#ffb4ab', letterSpacing: '0.08em', flexShrink: 0 }}>{t('evdetail_cancelled').toUpperCase()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Fahrgemeinschaft (mobile) ── */}
        {participatingRegs.length > 0 && (
          <section style={{ paddingBottom: 20 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495', marginBottom: 10 }}>FAHRGEMEINSCHAFT</div>

            {/* Own transport setter */}
            {ownIsIn && !isPast && (
              <div className="ed-glass" style={{ marginBottom: 10, overflow: 'hidden', borderLeft: '3px solid rgba(var(--brand-primary-rgb),0.3)' }}>
                {!showCarpoolEdit ? (
                  <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>MEIN TRANSPORT</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: ownRidingWithDriver || ownFullReg?.transport_mode ? '#e2e2e8' : '#849495' }}>
                        {ownRidingWithDriver ? `🚗 fährt mit ${ownRidingWithDriver.name}` : ownFullReg?.transport_mode === 'driving' ? `🚗 Ich fahre${ownFullReg.transport_seats ? ` (${ownFullReg.transport_seats} Plätze)` : ''}` : ownFullReg?.transport_mode === 'seeking' ? '🔍 Suche Mitfahrt' : 'Noch keine Angabe'}
                      </div>
                    </div>
                    <button onClick={openCarpoolEdit} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: 'var(--brand-primary)', background: 'rgba(var(--brand-primary-rgb),0.08)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)', padding: '5px 10px', cursor: 'pointer', letterSpacing: '0.08em' }}>BEARB.</button>
                  </div>
                ) : (
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 8 }}>MEIN TRANSPORT</div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      {[['driving', '🚗 Ich fahre'], ['seeking', '🔍 Mitfahrt suchen']].map(([mode, label]) => {
                        const active = carpoolMode === mode;
                        return (
                          <button key={mode} onClick={() => setCarpoolMode(mode)} style={{ flex: 1, padding: '7px 4px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', background: active ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent', border: `1px solid ${active ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.35)'}`, color: active ? 'var(--brand-primary)' : '#849495', cursor: 'pointer' }}>{label}</button>
                        );
                      })}
                    </div>
                    {carpoolMode === 'driving' && (
                      <>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                          <input type="number" min="1" max="8" value={carpoolSeats} onChange={e => setCarpoolSeats(e.target.value)} placeholder="Plätze"
                            style={{ width: 70, padding: '6px 8px', background: 'rgba(17,19,24,0.9)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} />
                          <input type="time" value={carpoolTime} onChange={e => setCarpoolTime(e.target.value)}
                            style={{ flex: 1, padding: '6px 8px', background: 'rgba(17,19,24,0.9)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} />
                        </div>
                        <input type="text" value={carpoolFrom} onChange={e => setCarpoolFrom(e.target.value)} placeholder="Abfahrtsort (optional)"
                          style={{ width: '100%', padding: '6px 8px', background: 'rgba(17,19,24,0.9)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }} />
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={handleSaveTransport} disabled={carpoolPending || !carpoolMode}
                        style={{ flex: 1, padding: '7px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)', cursor: carpoolPending || !carpoolMode ? 'default' : 'pointer', opacity: !carpoolMode ? 0.4 : 1, letterSpacing: '0.1em' }}>
                        {carpoolPending ? '…' : 'SPEICHERN'}
                      </button>
                      <button onClick={() => setShowCarpoolEdit(false)} style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', background: 'transparent', border: '1px solid rgba(59,73,75,0.35)', cursor: 'pointer' }}>ABBRUCH</button>
                      {ownFullReg?.transport_mode && (
                        <button onClick={handleClearTransport} disabled={carpoolPending} style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#ffb4ab', background: 'transparent', border: '1px solid rgba(255,180,171,0.25)', cursor: 'pointer' }}>✕</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Driver list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {participatingRegs.map(reg => {
                const driver  = allDrivers.find(d => d.id === reg.driver_id);
                if (!driver) return null;
                const fullReg = fullEventRegs.find(r => r.id === reg.id);
                const isMe    = driver.id === effectiveDriverId;
                const isDriving = fullReg?.transport_mode === 'driving';
                const isSeeking = fullReg?.transport_mode === 'seeking';
                const cardRidingWithReg = fullReg?.riding_with ? fullEventRegs.find(r => r.id === fullReg.riding_with) : null;
                const cardRidingWithDriver = cardRidingWithReg ? allDrivers.find(d => d.id === cardRidingWithReg.driver_id) : null;
                const passengerRegs = fullEventRegs.filter(r => r.riding_with === reg.id);
                const passengers = passengerRegs.map(r => allDrivers.find(d => d.id === r.driver_id)).filter(Boolean);
                const bookedCount = passengerRegs.length;
                const freeSeats   = fullReg?.transport_seats != null ? fullReg.transport_seats - bookedCount : null;
                const amRidingWithThis = ownRidingWithId === reg.id;
                const canBook  = isDriving && !isMe && ownIsIn && !isPast && !amRidingWithThis && (freeSeats === null || freeSeats > 0);
                const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                return (
                  <div key={reg.id} className="ed-glass" style={{ padding: '10px 14px', borderLeft: isDriving ? '3px solid var(--brand-secondary)' : cardRidingWithDriver ? '3px solid rgba(var(--brand-primary-rgb),0.3)' : isSeeking ? '3px solid rgba(255,154,31,0.5)' : '3px solid rgba(59,73,75,0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <DriverAvatar driver={driver} size={30} bg={s.bg} border={s.border} color={s.color} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 2 }}>
                          <span style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: '#e2e2e8' }}>{driver.name}</span>
                          {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--brand-secondary)', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '0 4px' }}>ICH</span>}
                          {isDriving && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: 'var(--brand-secondary)', padding: '0 4px', background: 'rgba(var(--brand-secondary-rgb),0.08)' }}>🚗 FÄHRT</span>}
                          {isSeeking && !cardRidingWithDriver && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: '#ff9a1f', padding: '0 4px', background: 'rgba(255,154,31,0.08)' }}>🔍 SUCHT</span>}
                          {cardRidingWithDriver && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: 'var(--brand-primary)', padding: '0 4px', background: 'rgba(var(--brand-primary-rgb),0.08)' }}>🚗 MIT {cardRidingWithDriver.name.split(' ')[0].toUpperCase()}</span>}
                        </div>
                        {isDriving && (
                          <>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495' }}>
                              {freeSeats !== null && `${freeSeats} Platz${freeSeats !== 1 ? 'e' : ''} frei`}
                              {fullReg?.transport_time && ` · ${fullReg.transport_time}`}
                            </div>
                            {fullReg?.transport_from && (
                              <div style={{ fontFamily: "var(--font-display)", fontSize: 11, color: '#b9cacb', marginTop: 2 }}>
                                📍 {fullReg.transport_from}
                              </div>
                            )}
                            {passengers.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 5 }}>
                                {passengers.map(p => {
                                  const ps = CAT_STYLE[p.category || 'Elite'] || CAT_STYLE.Elite;
                                  return <DriverAvatar key={p.id} driver={p} size={20} bg={ps.bg} border={ps.border} color={ps.color} />;
                                })}
                                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#849495', marginLeft: 4 }}>
                                  {passengers.map(p => p.name.split(' ')[0]).join(', ')}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                        {amRidingWithThis && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--brand-secondary)', marginTop: 2 }}>✓ du fährst mit</div>}
                      </div>
                      {canBook && (
                        <button onClick={() => handleRideWith(reg.id)} disabled={carpoolPending}
                          style={{ flexShrink: 0, padding: '5px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: 'var(--brand-secondary)', cursor: 'pointer', letterSpacing: '0.06em' }}>
                          {carpoolPending ? '…' : 'MITFAHREN'}
                        </button>
                      )}
                      {amRidingWithThis && ownIsIn && !isPast && (
                        <button onClick={handleCancelRide} disabled={carpoolPending}
                          style={{ flexShrink: 0, padding: '5px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, background: 'transparent', border: '1px solid rgba(255,180,171,0.25)', color: '#ffb4ab', cursor: 'pointer' }}>
                          {carpoolPending ? '…' : '✕'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Admin: Reminder ── */}
        {isAdminOrCoAdmin && unregisteredDrivers.length > 0 && (
          <section style={{ paddingBottom: 20 }}>
            <button
              onClick={handleRemind}
              disabled={reminding}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'rgba(var(--brand-secondary-rgb),0.07)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: 'var(--brand-secondary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', cursor: reminding ? 'wait' : 'pointer', opacity: reminding ? 0.6 : 1, transition: 'all 0.15s' }}
            >
              <span>🔔 {t('evdetail_send_reminder').toUpperCase()}</span>
              <span style={{ fontSize: 9, color: 'rgba(var(--brand-secondary-rgb),0.7)' }}>{unregisteredDrivers.length} {t('evdetail_no_answer').toUpperCase()}</span>
            </button>
          </section>
        )}

        {/* ── External link ── */}
        {event.url && (
          <a href={event.url} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '14px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', textDecoration: 'none', marginBottom: 24 }}>
            ☐ {t('evdetail_race_flyer').toUpperCase()} →
          </a>
        )}

        {/* ── Admin: Ergebnisse (mobile) ── */}
        {isPast && isAdminOrCoAdmin && participatingRegs.length > 0 && (
          <section style={{ paddingBottom: 24 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495', marginBottom: 10 }}>ERGEBNISSE</div>
            <div className="ed-glass" style={{ overflow: 'hidden' }}>
              {participatingRegs.map((reg, i) => {
                const driver = allDrivers.find(d => d.id === reg.driver_id);
                if (!driver) return null;
                const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                const draft = resultDraft[driver.id] || {};
                const saved = eventResults.find(r => r.driver_id === driver.id);
                return (
                  <div key={driver.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: i === 0 ? 'none' : '1px solid rgba(59,73,75,0.15)' }}>
                    <DriverAvatar driver={driver} size={28} bg={s.bg} border={s.border} color={s.color} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 600, color: '#e2e2e8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{driver.name}</div>
                      {saved?.points > 0 && (
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#ff9a1f' }}>★ {saved.points} Pkt{saved.position ? ` · P${saved.position}` : ''}</div>
                      )}
                    </div>
                    <input
                      type="number" min="1" placeholder="Platz"
                      value={draft.position ?? ''}
                      onChange={e => setResultDraft(prev => ({ ...prev, [driver.id]: { ...prev[driver.id], position: e.target.value } }))}
                      style={{ width: 52, padding: '5px 6px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(30,32,36,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', textAlign: 'center' }}
                    />
                    <input
                      type="number" min="0" placeholder="Pkt"
                      value={draft.points ?? ''}
                      onChange={e => setResultDraft(prev => ({ ...prev, [driver.id]: { ...prev[driver.id], points: e.target.value } }))}
                      style={{ width: 52, padding: '5px 6px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(30,32,36,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#ff9a1f', textAlign: 'center' }}
                    />
                    <button
                      onClick={() => handleSaveResult(driver.id)}
                      disabled={resultSaving}
                      style={{ padding: '5px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.3)', color: '#ff9a1f', cursor: resultSaving ? 'wait' : 'pointer' }}
                    >
                      ✓
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

      </div>

      {/* ══════════════════════ DESKTOP ══════════════════════ */}
      <div className="ed-desktop" style={{ display: 'none' }}>

        {/* Back */}
        <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--on-surface-variant)', padding: 0, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          ← {t('evdetail_back').toUpperCase()}
        </button>

        {/* ── Event Hero ── */}
        <div style={{ background: event.is_cancelled ? 'linear-gradient(135deg, rgba(40,0,0,0.97) 0%, rgba(25,4,4,0.98) 50%, rgba(17,19,24,1) 100%)' : event.is_mandatory ? 'linear-gradient(135deg, rgba(40,20,0,0.97) 0%, rgba(25,12,0,0.98) 50%, rgba(17,19,24,1) 100%)' : 'linear-gradient(135deg, rgba(0,36,40,0.95) 0%, rgba(0,20,30,0.98) 50%, rgba(17,19,24,1) 100%)', border: `1px solid ${event.is_cancelled ? 'rgba(220,50,50,0.4)' : event.is_mandatory ? 'rgba(255,160,50,0.35)' : 'rgba(var(--brand-primary-rgb),0.2)'}`, padding: '2rem 2.5rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(var(--brand-primary-rgb),0.025) 40px), repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(var(--brand-primary-rgb),0.015) 80px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Status + track_type badges */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {event.is_cancelled && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: '#dc2626', color: '#fff' }}>
                  ✕ ABGESAGT
                </span>
              )}
              {event.is_mandatory && !event.is_cancelled && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: '#ff9a1f', color: '#1a0800' }}>
                  ⚠ {t('evdetail_mandatory').toUpperCase()}
                </span>
              )}
              {(isPast || isToday) && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '3px 10px', background: isPast ? 'rgba(59,73,75,0.3)' : 'rgba(var(--brand-primary-rgb),0.1)', border: `1px solid ${isPast ? 'rgba(59,73,75,0.4)' : 'rgba(var(--brand-primary-rgb),0.3)'}`, color: isPast ? '#849495' : 'var(--brand-primary)' }}>
                  {isPast ? t('evdetail_past').toUpperCase() : `● ${t('evdetail_today').toUpperCase()}`}
                </span>
              )}
              {event.track_type && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 10px', border: '1px solid rgba(59,73,75,0.5)', color: '#849495' }}>
                  {event.track_type.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '3rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 280 }}>
                <h1 style={{ fontFamily: "var(--font-display)", fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, fontStyle: 'italic', color: event.is_cancelled ? 'rgba(220,100,100,0.75)' : '#ffffff', margin: '0 0 1rem 0', lineHeight: 1.0, letterSpacing: '0.03em', textTransform: 'uppercase', textShadow: event.is_cancelled ? 'none' : '0 0 40px rgba(var(--brand-primary-rgb),0.15)', textDecoration: event.is_cancelled ? 'line-through' : 'none' }}>
                  {event.name}
                </h1>
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: event.description ? 16 : 0 }}>
                  <div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>{t('evdetail_field_date').toUpperCase()}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: 'var(--brand-primary)' }}>{fmtShort(event.date)} {fmtYear(event.date)}</div>
                  </div>
                  {event.location && (
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>{t('evdetail_field_location').toUpperCase()}</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: '#e2e2e8' }}>{event.location.toUpperCase()}</div>
                    </div>
                  )}
                  {event.start_address && (
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 6 }}>START / ZIEL</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: '#e2e2e8', marginBottom: 8 }}>{event.start_address}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.start_address)}`} target="_blank" rel="noopener noreferrer"
                          style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--brand-primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '4px 10px', textDecoration: 'none' }}>
                          ↗ APPLE MAPS
                        </a>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.start_address)}`} target="_blank" rel="noopener noreferrer"
                          style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--brand-primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', padding: '4px 10px', textDecoration: 'none' }}>
                          ↗ GOOGLE MAPS
                        </a>
                      </div>
                    </div>
                  )}
                  {event.track_length && (
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>{t('evdetail_field_length').toUpperCase()}</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: '#e2e2e8' }}>{Number(event.track_length).toLocaleString('de-DE')} m</div>
                    </div>
                  )}
                </div>
                {event.description && <p style={{ fontFamily: "var(--font-display)", fontSize: 15, color: '#b9cacb', lineHeight: 1.6, margin: 0, maxWidth: 500 }}>{event.description}</p>}
                {/* ── Weather ── */}
                <WeatherWidget event={event} token={token} />
              </div>
              {/* Stats box */}
              <div style={{ background: 'rgba(17,19,24,0.7)', border: '1px solid rgba(59,73,75,0.4)', backdropFilter: 'blur(8px)', padding: '1.25rem 1.5rem', minWidth: 180 }}>
                {[
                  { label: t('evdetail_stat_registered').toUpperCase(), value: participatingRegs.length, color: 'var(--brand-primary)' },
                  { label: t('evdetail_stat_open').toUpperCase(),      value: unregisteredDrivers.length, color: '#849495' },
                  { label: t('evdetail_stat_classes').toUpperCase(),    value: evCats.length || '—',       color: 'var(--on-surface)' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ marginBottom: 12 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.12em', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color, lineHeight: 1 }}>{value}</div>
                  </div>
                ))}
                {event.url && (
                  <a href={event.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 16, padding: '8px 12px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textDecoration: 'none' }}>
                    ☐ {t('evdetail_race_flyer').toUpperCase()}
                  </a>
                )}
                <button onClick={() => setShowCalPrompt(true)} style={{ width: '100%', marginTop: 8, padding: '8px 12px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  ＋ KALENDER
                </button>
                <button onClick={handleShare} style={{ width: '100%', marginTop: 8, padding: '8px 12px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  ⎗ {t('evdetail_share').toUpperCase()}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main grid: schedule | registration + squad ── */}
        <div style={{ display: 'grid', gridTemplateColumns: evCats.length > 0 ? '240px 320px 1fr' : '320px 1fr', gap: '1.25rem', alignItems: 'start' }}>

          {/* LEFT: Event Schedule */}
          {evCats.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', position: 'sticky', top: 80 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#849495' }}>EVENT SCHEDULE</span>
              </div>
              {/* Timeline */}
              <div style={{ position: 'relative', paddingLeft: 20 }}>
                {/* Vertical line */}
                <div style={{ position: 'absolute', left: 7, top: 10, bottom: 10, width: 1, background: 'rgba(59,73,75,0.5)' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sortedCats.map(({ category, start_time }) => {
                    const s = schedCatStyle(category);
                    const isActive = catFilter === category;
                    return (
                      <div
                        key={category}
                        onClick={() => setCatFilter(isActive ? null : category)}
                        style={{ position: 'relative', cursor: 'pointer' }}
                      >
                        {/* Timeline dot — filled when active */}
                        <div style={{ position: 'absolute', left: -16, top: 14, width: 8, height: 8, borderRadius: '50%', background: isActive ? s.color : 'rgba(59,73,75,0.6)', border: `1px solid ${s.color}`, transition: 'background 0.15s' }} />
                        <div
                          style={{ background: isActive ? s.bg : 'rgba(17,19,24,0.6)', border: `1px solid ${isActive ? s.border : 'rgba(59,73,75,0.3)'}`, padding: '10px 14px', transition: 'all 0.15s', outline: isActive ? `1px solid ${s.color}` : 'none', outlineOffset: -1 }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = s.border; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'rgba(59,73,75,0.3)'; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: s.color, letterSpacing: '0.1em' }}>{category.toUpperCase()}</span>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: start_time ? (isActive ? s.color : '#ffffff') : '#849495', letterSpacing: '-0.02em' }}>
                              {start_time || '—'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: isActive ? '#e2e2e8' : '#849495' }}>{category}</div>
                            {isActive && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, color: s.color, letterSpacing: '0.1em', opacity: 0.8 }}>● FILTER</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* MIDDLE: An-/Abmeldung */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Fahrer: eigene Anmeldung (Co-Admin + racing owner können sich auch selbst anmelden) */}
            {(!isAdmin || isCoAdmin || (isAdmin && effectiveDriverId)) && (
              <div style={{ background: 'rgba(17,19,24,0.8)', border: '1px solid rgba(59,73,75,0.3)', overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 12 }}>{t('evdetail_my_reg').toUpperCase()}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, background: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).bg, border: `1px solid ${(CAT_STYLE[ownCategory] || CAT_STYLE.Elite).border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 800, color: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).color, flexShrink: 0 }}>
                      {ownDriver?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??'}
                    </div>
                    <div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 600, color: '#e2e2e8' }}>{ownDriver?.name || '—'}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: (CAT_STYLE[ownCategory] || CAT_STYLE.Elite).color, letterSpacing: '0.08em', marginTop: 2 }}>{ownCategory.toUpperCase()}</div>
                    </div>
                    {ownIsIn && (
                      <div style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: 'var(--brand-secondary)' }}>✓</div>
                    )}
                  </div>
                </div>
                {isPast ? (
                  <div style={{ padding: '12px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', letterSpacing: '0.1em' }}>
                    {ownIsIn ? (isToday ? `✓ ${t('evdetail_is_reg').toUpperCase()}` : `✓ ${t('evdetail_was_reg').toUpperCase()}`) : `— ${t('evdetail_not_reg').toUpperCase()}`}
                  </div>
                ) : ownIsIn ? (
                  <button onClick={handleOwnToggle} disabled={opPending === effectiveDriverId} className="ed-btn-unreg">
                    {opPending === effectiveDriverId ? '…' : `✕  ${t('evdetail_unregister_short').toUpperCase()}`}
                  </button>
                ) : ownReg?.status === 'not_participating' ? (
                  <button onClick={() => handleRegister(effectiveDriverId, 'participating')} disabled={opPending === effectiveDriverId} className="ed-btn-reg">
                    {opPending === effectiveDriverId ? '…' : `+ ${t('evdetail_register_anyway').toUpperCase()}`}
                  </button>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <button onClick={() => handleRegister(effectiveDriverId, 'participating')} disabled={opPending === effectiveDriverId} className="ed-btn-reg" style={{ flex: 1, borderRight: '1px solid rgba(59,73,75,0.4)' }}>
                      {opPending === effectiveDriverId ? '…' : `✓ ${t('evdetail_in').toUpperCase()}`}
                    </button>
                    <button onClick={() => handleRegister(effectiveDriverId, 'not_participating')} disabled={opPending === effectiveDriverId} className="ed-btn-unreg" style={{ flex: 1 }}>
                      {opPending === effectiveDriverId ? '…' : `✕ ${t('evdetail_out').toUpperCase()}`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Admin: schnelles Hinzufügen */}
            {isAdminOrCoAdmin && (
              <div style={{ background: 'rgba(17,19,24,0.8)', border: '1px solid rgba(59,73,75,0.3)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(59,73,75,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)' }}>
                    {t('evdetail_register_rider').toUpperCase()}
                  </span>
                  {unregisteredDrivers.length > 0 && (
                    <button
                      onClick={handleRemind}
                      disabled={reminding}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: 'var(--brand-secondary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', cursor: reminding ? 'wait' : 'pointer', opacity: reminding ? 0.6 : 1, transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                      title={`${t('evdetail_remind_title')} ${unregisteredDrivers.length}`}
                    >
                      🔔 {reminding ? t('evdetail_sending').toUpperCase() : `${t('evdetail_remind').toUpperCase()} (${unregisteredDrivers.length})`}
                    </button>
                  )}
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', padding: '8px 0' }}>
                  {notParticipating.filter(reg => {
                    if (!catFilter) return true;
                    const d = allDrivers.find(dr => dr.id === reg.driver_id);
                    return driverCatMatchesScheduleCat(d?.category || 'Elite', catFilter);
                  }).map(reg => {
                    const driver = allDrivers.find(d => d.id === reg.driver_id);
                    if (!driver) return null;
                    const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                    const meta = { color: '#ffb4ab', label: t('evdetail_not_reg').toUpperCase() };
                    return (
                      <div key={reg.id} className="ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 16px', background: 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#e2e2e8' }}>{driver.name}</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: meta.color, letterSpacing: '0.08em' }}>{meta.label}</div>
                          </div>
                        </div>
                        <button onClick={() => handleAdminStatusChange(driver, 'participating')} disabled={opPending === driver.id} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', padding: '3px 8px', cursor: 'pointer' }}>
                          {opPending === driver.id ? '…' : t('evdetail_register_btn').toUpperCase()}
                        </button>
                      </div>
                    );
                  })}
                  {/* Nicht registrierte — gefiltert nach catFilter */}
                  {unregisteredDrivers.filter(d => !catFilter || driverCatMatchesScheduleCat(d.category || 'Elite', catFilter)).map(driver => {
                    const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                    return (
                      <div key={driver.id} className="ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 16px', background: 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#b9cacb' }}>{driver.name}</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: s.color, letterSpacing: '0.08em' }}>{(driver.category || 'Elite').toUpperCase()}</div>
                          </div>
                        </div>
                        <button onClick={() => handleAdminStatusChange(driver, 'participating')} disabled={opPending === driver.id} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', color: 'var(--brand-primary)', padding: '3px 8px', cursor: 'pointer' }}>
                          {opPending === driver.id ? '…' : `+ ${t('evdetail_register_btn').toUpperCase()}`}
                        </button>
                      </div>
                    );
                  })}
                  {unregisteredDrivers.length === 0 && pendingRegs.length === 0 && notParticipating.length === 0 && (
                    <div style={{ padding: '12px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em' }}>{t('evdetail_all_registered').toUpperCase()} ✓</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Squad + Admin status management */}
          <div style={{ background: 'rgba(17,19,24,0.8)', border: '1px solid rgba(59,73,75,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(59,73,75,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)' }}>
                {isAdminOrCoAdmin ? t('evdetail_all_riders_status').toUpperCase() : `SQUAD — ${participatingRegs.length} ${t('evdetail_rider_count').toUpperCase()}`}
              </span>
              {isAdminOrCoAdmin && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--brand-primary)', letterSpacing: '0.08em' }}>
                  {participatingRegs.length}/{allDrivers.length} {t('evdetail_stat_registered').toUpperCase()}
                </span>
              )}
            </div>


          {isAdminOrCoAdmin ? (
              /* Admin/Co-Admin view: grouped by category, pending first then registered then declined */
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {CATEGORIES.map(cat => {
                  const s = CAT_STYLE[cat] || CAT_STYLE.Elite;
                  const driversInCat = allDrivers.filter(d => (d.category || 'Elite') === cat);
                  if (driversInCat.length === 0) return null;
                  if (catFilter && !driverCatMatchesScheduleCat(cat, catFilter)) return null;
                  const pending  = driversInCat.filter(d => { const r = getRegFor(d.id); return !r || (r.status !== 'participating' && r.status !== 'assigned' && r.status !== 'not_participating'); });
                  const inCat    = driversInCat.filter(d => { const r = getRegFor(d.id); return r?.status === 'participating' || r?.status === 'assigned'; });
                  const outCat   = driversInCat.filter(d => { const r = getRegFor(d.id); return r?.status === 'not_participating'; });
                  const ordered  = [...pending, ...inCat, ...outCat];
                  return (
                    <div key={cat}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: s.bg, borderBottom: `1px solid ${s.border}`, borderTop: '1px solid rgba(59,73,75,0.1)' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: s.color }}>{cat.toUpperCase()}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: s.color, opacity: 0.6, marginLeft: 'auto' }}>
                          {inCat.length}/{driversInCat.length}
                        </span>
                      </div>
                      {ordered.map((driver, idx) => {
                        const reg    = getRegFor(driver.id);
                        const status = reg?.status ?? null;
                        const isIn   = status === 'participating' || status === 'assigned';
                        const isOut  = status === 'not_participating';
                        const rowBg  = idx % 2 === 0 ? 'rgba(17,19,24,0.5)' : 'rgba(26,28,32,0.3)';
                        return (
                          <div key={driver.id} className="ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', background: rowBg, borderBottom: '1px solid rgba(59,73,75,0.08)', opacity: isOut ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                              <DriverAvatar driver={driver} size={30} bg={isIn ? s.bg : isOut ? 'rgba(255,180,171,0.07)' : 'rgba(40,42,46,0.8)'} border={isIn ? s.border : isOut ? 'rgba(255,180,171,0.2)' : 'rgba(59,73,75,0.3)'} color={isIn ? s.color : isOut ? '#ffb4ab' : '#849495'} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: isIn ? '#e2e2e8' : '#849495', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isOut ? 'line-through' : 'none' }}>{driver.name}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              {ADMIN_STATUSES.map(({ value, label, color, icon }) => {
                                const isActive = status === value || (value === null && status === null);
                                return (
                                  <button
                                    key={String(value)}
                                    onClick={() => !isActive && handleAdminStatusChange(driver, value)}
                                    disabled={opPending === driver.id || isActive}
                                    title={label}
                                    style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, background: isActive ? (value === null ? 'transparent' : `rgba(${value === 'participating' ? '0,219,233' : '255,180,171'},0.15)`) : 'transparent', border: `1px solid ${isActive ? color : 'rgba(59,73,75,0.3)'}`, color: isActive ? color : 'rgba(132,148,149,0.4)', cursor: isActive ? 'default' : 'pointer', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!isActive && opPending !== driver.id) { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; } }}
                                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'rgba(59,73,75,0.3)'; e.currentTarget.style.color = 'rgba(132,148,149,0.4)'; } }}
                                  >
                                    {opPending === driver.id ? '…' : icon}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Driver/viewer: squad grouped by category, incl. absagen */
              <div>
                {CATEGORIES.map(cat => {
                  const s = CAT_STYLE[cat] || CAT_STYLE.Elite;
                  const catIn  = participatingRegs.filter(r => (allDrivers.find(d => d.id === r.driver_id)?.category || 'Elite') === cat);
                  const catOut = notParticipating.filter(r => (allDrivers.find(d => d.id === r.driver_id)?.category || 'Elite') === cat);
                  if (catIn.length === 0 && catOut.length === 0) return null;
                  if (catFilter && !driverCatMatchesScheduleCat(cat, catFilter)) return null;
                  return (
                    <div key={cat}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: s.bg, borderBottom: `1px solid ${s.border}`, borderTop: '1px solid rgba(59,73,75,0.1)' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: s.color }}>{cat.toUpperCase()}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: s.color, opacity: 0.6, marginLeft: 'auto' }}>{catIn.length} dabei</span>
                      </div>
                      {catIn.map((reg, idx) => {
                        const driver = allDrivers.find(d => d.id === reg.driver_id);
                        if (!driver) return null;
                        const isMe  = driver.id === driverId;
                        const rowBg = idx % 2 === 0 ? 'rgba(17,19,24,0.5)' : 'rgba(26,28,32,0.3)';
                        return (
                          <div key={reg.id} className="ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', background: rowBg, borderBottom: '1px solid rgba(59,73,75,0.08)', borderLeft: isMe ? '2px solid var(--brand-secondary)' : '2px solid transparent' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <DriverAvatar driver={driver} size={26} bg={s.bg} border={s.border} color={s.color} />
                              <span style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#e2e2e8' }}>{driver.name}</span>
                              {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--brand-secondary)', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '0 4px' }}>ICH</span>}
                            </div>
                          </div>
                        );
                      })}
                      {catOut.map((reg, idx) => {
                        const driver = allDrivers.find(d => d.id === reg.driver_id);
                        if (!driver) return null;
                        const isMe  = driver.id === driverId;
                        const rowBg = idx % 2 === 0 ? 'rgba(17,19,24,0.5)' : 'rgba(26,28,32,0.3)';
                        return (
                          <div key={reg.id} className="ed-driver-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', background: rowBg, borderBottom: '1px solid rgba(59,73,75,0.08)', borderLeft: isMe ? '2px solid #ffb4ab' : '2px solid transparent', opacity: 0.6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <DriverAvatar driver={driver} size={26} bg="rgba(255,180,171,0.06)" border="rgba(255,180,171,0.2)" color="#ffb4ab" />
                              <span style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#849495', textDecoration: 'line-through' }}>{driver.name}</span>
                              {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#ffb4ab', background: 'rgba(255,180,171,0.1)', border: '1px solid rgba(255,180,171,0.2)', padding: '0 4px' }}>ICH</span>}
                            </div>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: '#ffb4ab', letterSpacing: '0.08em' }}>ABGESAGT</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* ── Fahrgemeinschaft (desktop) ── */}
        {participatingRegs.length > 0 && (
          <div style={{ marginTop: '1.25rem', background: 'rgba(17,19,24,0.8)', border: '1px solid rgba(59,73,75,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(59,73,75,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)' }}>FAHRGEMEINSCHAFT</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495' }}>
                {fullEventRegs.filter(r => r.transport_mode === 'driving').length} Fahrer · {fullEventRegs.filter(r => r.transport_mode === 'seeking').length} suchen Mitfahrt
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: ownIsIn && !isPast ? '280px 1fr' : '1fr', gap: 0 }}>
              {/* Left: own transport setter */}
              {ownIsIn && !isPast && (
                <div style={{ borderRight: '1px solid rgba(59,73,75,0.2)', padding: '14px 16px' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 10 }}>MEIN TRANSPORT</div>
                  {!showCarpoolEdit ? (
                    <>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 14, color: ownRidingWithDriver || ownFullReg?.transport_mode ? '#e2e2e8' : '#849495', marginBottom: 10 }}>
                        {ownRidingWithDriver ? `🚗 fährt mit ${ownRidingWithDriver.name}` : ownFullReg?.transport_mode === 'driving' ? `🚗 Ich fahre${ownFullReg.transport_seats ? ` · ${ownFullReg.transport_seats} Plätze` : ''}${ownFullReg.transport_time ? ` · ${ownFullReg.transport_time}` : ''}${ownFullReg.transport_from ? ` · ${ownFullReg.transport_from}` : ''}` : ownFullReg?.transport_mode === 'seeking' ? '🔍 Ich suche Mitfahrt' : 'Noch keine Angabe'}
                      </div>
                      <button onClick={openCarpoolEdit} style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: 'var(--brand-primary)', background: 'rgba(var(--brand-primary-rgb),0.08)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)', cursor: 'pointer', letterSpacing: '0.1em' }}>BEARBEITEN</button>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                        {[['driving', '🚗 Ich fahre'], ['seeking', '🔍 Mitfahrt suchen']].map(([mode, label]) => {
                          const active = carpoolMode === mode;
                          return (
                            <button key={mode} onClick={() => setCarpoolMode(mode)} style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textAlign: 'left', background: active ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent', border: `1px solid ${active ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.35)'}`, color: active ? 'var(--brand-primary)' : '#849495', cursor: 'pointer' }}>{label}</button>
                          );
                        })}
                      </div>
                      {carpoolMode === 'driving' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input type="number" min="1" max="8" value={carpoolSeats} onChange={e => setCarpoolSeats(e.target.value)} placeholder="Plätze"
                              style={{ width: 70, padding: '6px 8px', background: 'rgba(12,14,18,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} />
                            <input type="time" value={carpoolTime} onChange={e => setCarpoolTime(e.target.value)}
                              style={{ flex: 1, padding: '6px 8px', background: 'rgba(12,14,18,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} />
                          </div>
                          <input type="text" value={carpoolFrom} onChange={e => setCarpoolFrom(e.target.value)} placeholder="Abfahrtsort (optional)"
                            style={{ padding: '6px 8px', background: 'rgba(12,14,18,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 12 }} />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={handleSaveTransport} disabled={carpoolPending || !carpoolMode}
                          style={{ flex: 1, padding: '7px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)', cursor: carpoolPending || !carpoolMode ? 'default' : 'pointer', opacity: !carpoolMode ? 0.4 : 1, letterSpacing: '0.1em' }}>
                          {carpoolPending ? '…' : 'SPEICHERN'}
                        </button>
                        <button onClick={() => setShowCarpoolEdit(false)} style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', background: 'transparent', border: '1px solid rgba(59,73,75,0.35)', cursor: 'pointer' }}>ABB.</button>
                        {ownFullReg?.transport_mode && (
                          <button onClick={handleClearTransport} disabled={carpoolPending} style={{ padding: '7px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#ffb4ab', background: 'transparent', border: '1px solid rgba(255,180,171,0.25)', cursor: 'pointer' }}>✕</button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Right: driver list */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '14px 16px', alignContent: 'flex-start' }}>
                {participatingRegs.map(reg => {
                  const driver  = allDrivers.find(d => d.id === reg.driver_id);
                  if (!driver) return null;
                  const fullReg = fullEventRegs.find(r => r.id === reg.id);
                  const isMe    = driver.id === effectiveDriverId;
                  const isDriving = fullReg?.transport_mode === 'driving';
                  const isSeeking = fullReg?.transport_mode === 'seeking';
                  const cardRidingWithReg = fullReg?.riding_with ? fullEventRegs.find(r => r.id === fullReg.riding_with) : null;
                  const cardRidingWithDriver = cardRidingWithReg ? allDrivers.find(d => d.id === cardRidingWithReg.driver_id) : null;
                  const passengerRegs = fullEventRegs.filter(r => r.riding_with === reg.id);
                  const passengers = passengerRegs.map(r => allDrivers.find(d => d.id === r.driver_id)).filter(Boolean);
                  const bookedCount = passengerRegs.length;
                  const freeSeats   = fullReg?.transport_seats != null ? fullReg.transport_seats - bookedCount : null;
                  const amRidingWithThis = ownRidingWithId === reg.id;
                  const canBook  = isDriving && !isMe && ownIsIn && !isPast && !amRidingWithThis && (freeSeats === null || freeSeats > 0);
                  const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                  return (
                    <div key={reg.id} style={{ background: 'rgba(26,28,32,0.6)', border: `1px solid ${isDriving ? 'rgba(var(--brand-secondary-rgb),0.25)' : cardRidingWithDriver ? 'rgba(var(--brand-primary-rgb),0.2)' : isSeeking ? 'rgba(255,154,31,0.25)' : 'rgba(59,73,75,0.3)'}`, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 170 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DriverAvatar driver={driver} size={26} bg={s.bg} border={s.border} color={s.color} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 600, color: '#e2e2e8', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {driver.name}
                            {isMe && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, color: 'var(--brand-secondary)', background: 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.2)', padding: '0 3px' }}>ICH</span>}
                          </div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: isDriving ? 'var(--brand-secondary)' : cardRidingWithDriver ? 'var(--brand-primary)' : isSeeking ? '#ff9a1f' : '#849495', marginTop: 2 }}>
                            {isDriving ? `🚗 ${freeSeats !== null ? `${freeSeats} frei` : 'fährt'}${fullReg?.transport_time ? ` · ${fullReg.transport_time}` : ''}` : cardRidingWithDriver ? `🚗 mit ${cardRidingWithDriver.name.split(' ')[0]}` : isSeeking ? '🔍 sucht Mitfahrt' : '—'}
                          </div>
                          {isDriving && fullReg?.transport_from && (
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 10, color: '#b9cacb', marginTop: 2 }}>
                              📍 {fullReg.transport_from}
                            </div>
                          )}
                          {amRidingWithThis && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--brand-secondary)', marginTop: 1 }}>✓ mitfahren</div>}
                        </div>
                        {canBook && (
                          <button onClick={() => handleRideWith(reg.id)} disabled={carpoolPending}
                            style={{ padding: '4px 7px', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: 'var(--brand-secondary)', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
                            {carpoolPending ? '…' : 'MITFAHREN'}
                          </button>
                        )}
                        {amRidingWithThis && ownIsIn && !isPast && (
                          <button onClick={handleCancelRide} disabled={carpoolPending}
                            style={{ padding: '4px 7px', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, background: 'transparent', border: '1px solid rgba(255,180,171,0.25)', color: '#ffb4ab', cursor: 'pointer', alignSelf: 'flex-start' }}>
                            {carpoolPending ? '…' : '✕'}
                          </button>
                        )}
                      </div>
                      {/* Passenger avatars */}
                      {isDriving && passengers.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingTop: 4, borderTop: '1px solid rgba(59,73,75,0.2)' }}>
                          {passengers.map(p => {
                            const ps = CAT_STYLE[p.category || 'Elite'] || CAT_STYLE.Elite;
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <DriverAvatar driver={p} size={18} bg={ps.bg} border={ps.border} color={ps.color} />
                              </div>
                            );
                          })}
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, color: '#849495', marginLeft: 2 }}>
                            {passengers.map(p => p.name.split(' ')[0]).join(' · ')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Admin: Ergebnisse (desktop) ── */}
        {isPast && isAdminOrCoAdmin && participatingRegs.length > 0 && (
          <div style={{ marginTop: '1.5rem', background: 'rgba(17,19,24,0.7)', border: '1px solid rgba(255,154,31,0.2)', padding: '1.5rem' }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 800, fontStyle: 'italic', color: '#ff9a1f', marginBottom: '1rem', letterSpacing: '0.05em' }}>
              ★ RENNERGEBNISSE
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
              {participatingRegs.map(reg => {
                const driver = allDrivers.find(d => d.id === reg.driver_id);
                if (!driver) return null;
                const s = CAT_STYLE[driver.category || 'Elite'] || CAT_STYLE.Elite;
                const draft = resultDraft[driver.id] || {};
                const saved = eventResults.find(r => r.driver_id === driver.id);
                return (
                  <div key={driver.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: saved?.points > 0 ? 'rgba(255,154,31,0.05)' : 'rgba(30,32,36,0.5)', border: `1px solid ${saved?.points > 0 ? 'rgba(255,154,31,0.2)' : 'rgba(59,73,75,0.25)'}` }}>
                    <DriverAvatar driver={driver} size={32} bg={s.bg} border={s.border} color={s.color} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: '#e2e2e8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{driver.name}</div>
                      {saved?.points > 0 && (
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#ff9a1f' }}>★ {saved.points} Pkt{saved.position ? ` · Platz ${saved.position}` : ''}</div>
                      )}
                    </div>
                    <input
                      type="number" min="1" placeholder="Platz"
                      value={draft.position ?? ''}
                      onChange={e => setResultDraft(prev => ({ ...prev, [driver.id]: { ...prev[driver.id], position: e.target.value } }))}
                      style={{ width: 60, padding: '6px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, background: 'rgba(30,32,36,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', textAlign: 'center' }}
                    />
                    <input
                      type="number" min="0" placeholder="Pkt"
                      value={draft.points ?? ''}
                      onChange={e => setResultDraft(prev => ({ ...prev, [driver.id]: { ...prev[driver.id], points: e.target.value } }))}
                      style={{ width: 60, padding: '6px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, background: 'rgba(30,32,36,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#ff9a1f', textAlign: 'center' }}
                    />
                    <button
                      onClick={() => handleSaveResult(driver.id)}
                      disabled={resultSaving}
                      style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.35)', color: '#ff9a1f', cursor: resultSaving ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      SAVE
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default EventDetail;
